import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifecycleNodeKind } from '../../../../components/LinkedChips';
import { linkedNodesQueryKey } from '../../../../components/LinkedChips/useLinkedNodes';
import { projectDataClient } from '../../../../lib/connect/clients';
import { CAPTURE_KEY, PURSUITS_KEY } from '../../hooks';
import { PURSUIT_HEADER_KEY, PURSUIT_THREAD_KEY } from '../pursuitQueryKeys';
export const PURSUIT_SURGERY_KEY = 'pursuit-surgery';
/** The component kinds in picker order, with their group headings (GRW-19). */ export const SURGERY_KIND_GROUPS = [
    {
        kind: 'interaction',
        label: 'Touchpoints'
    },
    {
        kind: 'rfi_response_link',
        label: 'RFI responses'
    },
    {
        kind: 'workstream_item',
        label: 'Workstream items'
    },
    {
        kind: 'task',
        label: 'Tasks'
    },
    {
        kind: 'document',
        label: 'Documents'
    },
    {
        kind: 'teaming_link',
        label: 'Teaming'
    },
    {
        kind: 'competitive_participant',
        label: 'Competitors'
    },
    {
        kind: 'recompete_link',
        label: 'Recompete link'
    },
    {
        kind: 'stakeholder',
        label: 'People'
    },
    {
        kind: 'proposal_link',
        label: 'Proposal'
    }
];
/** M:N kinds that may be linked (kept on both pursuits) instead of moved. */ export const LINKABLE_KINDS = new Set([
    'rfi_response_link',
    'document',
    'recompete_link'
]);
/** Kinds the server can detach (document links have no tombstone, so never). */ export const DETACHABLE_KINDS = new Set([
    'rfi_response_link',
    'recompete_link'
]);
/** Default mode for a kind: Link for RFI responses (09:638 "prefer add link"), else Move. */ export function defaultSurgeryMode(kind) {
    return kind === 'rfi_response_link' ? 'link' : 'move';
}
/** True when the selection moves an RFI response or a proposal link (proposal review applies). */ export function selectionNeedsReview(components) {
    return components.some((c)=>c.kind === 'proposal_link' || c.kind === 'rfi_response_link' && c.mode === 'move');
}
function invalidateAfterSurgery(qc, ids) {
    return Promise.all([
        qc.invalidateQueries({
            queryKey: [
                PURSUIT_HEADER_KEY
            ]
        }),
        qc.invalidateQueries({
            queryKey: [
                PURSUIT_THREAD_KEY
            ]
        }),
        qc.invalidateQueries({
            queryKey: [
                PURSUITS_KEY
            ]
        }),
        qc.invalidateQueries({
            queryKey: [
                PURSUIT_SURGERY_KEY
            ]
        }),
        ...ids.filter(Boolean).flatMap((id)=>[
                qc.invalidateQueries({
                    queryKey: linkedNodesQueryKey(LifecycleNodeKind.PURSUIT, id)
                }),
                qc.invalidateQueries({
                    queryKey: [
                        CAPTURE_KEY,
                        id
                    ]
                })
            ])
    ]).then(()=>undefined);
}
/** GRW-18 `PlanPursuitSurgery`: what the pursuit could give away, with blocked reasons. */ export function usePursuitSurgeryPlan(captureProjectId, enabled) {
    return useQuery({
        queryKey: [
            PURSUIT_SURGERY_KEY,
            'plan',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.planPursuitSurgery({
                captureProjectId
            }),
        enabled: enabled && !!captureProjectId,
        retry: false
    });
}
/** GRW-18 `ListPursuitSurgery`: events where the pursuit is the source or the target. */ export function usePursuitSurgery(captureProjectId) {
    return useQuery({
        queryKey: [
            PURSUIT_SURGERY_KEY,
            'events',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.listPursuitSurgery({
                captureProjectId
            }),
        enabled: !!captureProjectId
    });
}
export function useSplitPursuit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args)=>projectDataClient.splitPursuit(args),
        onSuccess: (resp, vars)=>invalidateAfterSurgery(qc, [
                vars.sourceCaptureProjectId,
                resp.newCaptureProjectId
            ])
    });
}
export function useMergePursuits() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args)=>projectDataClient.mergePursuits(args),
        onSuccess: (_resp, vars)=>invalidateAfterSurgery(qc, [
                vars.loserCaptureProjectId,
                vars.survivorCaptureProjectId
            ])
    });
}
export function useMovePursuitComponents() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args)=>projectDataClient.movePursuitComponents(args),
        onSuccess: (_resp, vars)=>invalidateAfterSurgery(qc, [
                vars.fromCaptureProjectId,
                vars.toCaptureProjectId
            ])
    });
}
